import { Link, useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { ArrowLeft, LifeBuoy } from "lucide-react";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import {
  categoryLabel, TicketThread, TicketReplyComposer,
} from "@/pages/Support";

const STATUS_META = {
  open:          { label: "Abierto",         cls: "bg-blue-500/15 text-blue-600 dark:text-blue-300" },
  in_progress:   { label: "En atención",     cls: "bg-amber-500/15 text-amber-700 dark:text-amber-300" },
  waiting_user:  { label: "Esperando tu respuesta", cls: "bg-purple-500/15 text-purple-600 dark:text-purple-300" },
  resolved:      { label: "Resuelto",        cls: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-300" },
  closed:        { label: "Cerrado",         cls: "bg-slate-500/15 text-slate-600 dark:text-slate-300" },
};

export default function SupportTicket() {
  const { ticketId } = useParams();
  const navigate = useNavigate();

  const { data: ticket, isLoading } = useQuery({
    queryKey: ["ticket", ticketId],
    queryFn: async () => (await api.get(`/support/tickets/${ticketId}`)).data,
    refetchOnWindowFocus: true,
    refetchInterval: 30000, // gentle poll while the thread is open
  });

  if (isLoading) {
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-4">
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-24 rounded-2xl" />
        <Skeleton className="h-40 rounded-2xl" />
      </div>
    );
  }
  if (!ticket) {
    return (
      <div className="p-6 md:p-10 max-w-4xl mx-auto">
        <p className="text-muted-foreground">Ticket no encontrado.</p>
        <button onClick={() => navigate("/support")} className="text-blue-600 mt-2 text-sm">← Volver a soporte</button>
      </div>
    );
  }

  const meta = STATUS_META[ticket.status] || STATUS_META.open;
  const ref = "#" + String(ticket.ticket_number).padStart(4, "0");
  const disabled = ticket.status === "closed";

  return (
    <div className="h-full overflow-y-auto kanban-scroll" data-testid="support-ticket-page">
      <div className="max-w-4xl mx-auto p-6 md:p-10">
        <div className="flex items-center gap-2 mb-4 text-sm">
          <Link to="/support" data-testid="ticket-back" className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground transition">
            <ArrowLeft className="h-4 w-4" /> Mis tickets
          </Link>
        </div>

        <div className="glass-panel rounded-2xl overflow-hidden shadow-lg">
          <header className="p-5 md:p-6 border-b border-white/30 dark:border-white/10 flex items-start gap-3">
            <div className="h-11 w-11 rounded-xl bg-blue-500/15 grid place-items-center flex-none">
              <LifeBuoy className="h-5 w-5 text-blue-600 dark:text-blue-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono text-muted-foreground">{ref}</span>
                <span className={cn("text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full", meta.cls)}
                      data-testid="ticket-status-badge">
                  {meta.label}
                </span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                  {categoryLabel(ticket.category)}
                </span>
              </div>
              <h1 className="font-display text-xl md:text-2xl font-semibold tracking-tight mt-1 truncate" data-testid="ticket-subject">
                {ticket.subject}
              </h1>
            </div>
          </header>

          <TicketThread ticket={ticket} />

          <TicketReplyComposer
            ticketId={ticketId}
            disabled={disabled}
            endpoint={`/support/tickets/${ticketId}/messages`}
          />
        </div>
      </div>
    </div>
  );
}
